import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Users, Plus, Heart, ChatCircle, Flag, X, Image, GridFour, EnvelopeSimple } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Lightbox from '../components/Lightbox';
import UserAvatar from '../components/UserAvatar';

import { API_URL } from '../config';

const CommunityPage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [galleryPosts, setGalleryPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [viewMode, setViewMode] = useState('posts');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportPostId, setReportPostId] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [commentText, setCommentText] = useState('');
  const [commentImageUrl, setCommentImageUrl] = useState('');
  const [commentUploading, setCommentUploading] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');
  const [resending, setResending] = useState(false);

  const isVerified = user?.email_verified !== false;

  useEffect(() => {
    fetchPosts();
  }, [category]);

  useEffect(() => {
    if (viewMode === 'gallery') fetchGallery();
  }, [viewMode]);

  const fetchPosts = async () => {
    try {
      const url = category
        ? `${API_URL}/api/community/posts?category=${category}`
        : `${API_URL}/api/community/posts`;
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGallery = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/community/gallery`);
      setGalleryPosts(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/community/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImageUrl(res.data.image_url);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleCommentImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCommentUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/community/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCommentImageUrl(res.data.image_url);
    } catch (err) {
      console.error('Comment image upload failed:', err);
    } finally {
      setCommentUploading(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { content: postContent, image_url: imageUrl || null };
      await axios.post(`${API_URL}/api/community/posts`, payload, { withCredentials: true });
      setPostContent('');
      setImageUrl('');
      setDialogOpen(false);
      fetchPosts();
      if (viewMode === 'gallery') fetchGallery();
    } catch (error) {
      console.error('Error creating post:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await axios.post(`${API_URL}/api/community/posts/${postId}/like`, {}, { withCredentials: true });
      const updateList = (list) => list.map(p => p.id === postId ? { ...p, likes: response.data.likes } : p);
      setPosts(updateList);
      setGalleryPosts(updateList);
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const loadPostDetails = async (postId) => {
    try {
      const response = await axios.get(`${API_URL}/api/community/posts/${postId}`);
      setSelectedPost(response.data);
    } catch (error) {
      console.error('Error loading post:', error);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() && !commentImageUrl) return;
    try {
      await axios.post(`${API_URL}/api/community/posts/${selectedPost.id}/comments`,
        { content: commentText, image_url: commentImageUrl || null },
        { withCredentials: true }
      );
      setCommentText('');
      setCommentImageUrl('');
      loadPostDetails(selectedPost.id);
      fetchPosts();
    } catch (error) {
      console.error('Error commenting:', error);
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) return;
    try {
      await axios.post(`${API_URL}/api/community/posts/${reportPostId}/report`,
        { reason: reportReason },
        { withCredentials: true }
      );
      setReportDialogOpen(false);
      setReportPostId(null);
      setReportReason('');
      alert('Post reported. Thank you for helping keep our community safe.');
    } catch (error) {
      console.error('Error reporting:', error);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, {}, { withCredentials: true });
      alert('Verification email sent! Check your inbox.');
    } catch (err) {
      console.error('Resend failed:', err);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Email Verification Banner */}
        {user && !isVerified && (
          <div className="mb-6 p-4 bg-[#FF9500]/10 border border-[#FF9500]/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="verify-banner">
            <div className="flex items-center gap-3">
              <EnvelopeSimple size={20} className="text-[#FF9500] flex-shrink-0" />
              <p className="text-sm text-[#FF9500]">Verify your email to post and comment in the community.</p>
            </div>
            <button
              onClick={handleResendVerification}
              disabled={resending}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#FF9500] text-black hover:bg-[#e08600] transition-colors whitespace-nowrap disabled:opacity-50"
              data-testid="resend-verification-btn"
            >
              {resending ? 'Sending...' : 'Resend Email'}
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl uppercase text-white mb-2" data-testid="community-title">
              Community
            </h1>
            <p className="text-[#8892B0]">Share and learn from fellow traders</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button
                className={`inline-flex items-center gap-2 px-6 py-3 font-bold uppercase tracking-widest transition-colors ${
                  isVerified ? 'bg-[#007AFF] text-white hover:bg-[#005bb5]' : 'bg-[#5C6B82]/30 text-[#5C6B82] cursor-not-allowed'
                }`}
                disabled={!isVerified}
                data-testid="new-post-btn"
              >
                <Plus size={20} weight="bold" /> New Post
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#131313] border-white/10 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl uppercase">Create Post</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreatePost} className="space-y-4 mt-4">
                <div>
                  <Label className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">What's on your mind?</Label>
                  <Textarea
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                    placeholder="Share your thoughts, struggles, or wins..."
                    className="bg-transparent border-white/20 text-white rounded-none min-h-[120px]"
                    required
                    data-testid="post-content"
                  />
                </div>
                {/* Image Attachment */}
                <div>
                  <label
                    className="flex items-center gap-2 px-4 py-3 border border-dashed border-white/20 cursor-pointer hover:border-[#007AFF] transition-colors"
                    data-testid="image-upload-label"
                  >
                    <Image size={20} className="text-[#8892B0]" />
                    <span className="text-sm text-[#8892B0]">
                      {uploading ? 'Uploading...' : imageUrl ? 'Image attached — click to change' : 'Attach an image (optional)'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      data-testid="image-file-input"
                    />
                  </label>
                  {imageUrl && (
                    <div className="mt-2 relative inline-block">
                      <img src={imageUrl} alt="Preview" className="max-h-40 rounded border border-white/10 object-cover" />
                      <button
                        type="button"
                        onClick={() => setImageUrl('')}
                        className="absolute top-1 right-1 bg-black/70 p-1 hover:bg-red-600 transition-colors"
                        data-testid="remove-image-btn"
                      >
                        <X size={14} className="text-white" />
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors disabled:opacity-50"
                  data-testid="post-submit"
                >
                  {submitting ? 'Posting...' : 'Post'}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2 mb-6" data-testid="view-toggle">
          <button
            onClick={() => setViewMode('posts')}
            className={`px-5 py-2 text-sm uppercase tracking-wider font-bold transition-colors ${
              viewMode === 'posts' ? 'bg-[#007AFF] text-white' : 'bg-[#131313] text-[#8892B0] border border-white/10 hover:text-white'
            }`}
            data-testid="toggle-posts"
          >
            Posts
          </button>
          <button
            onClick={() => setViewMode('gallery')}
            className={`px-5 py-2 text-sm uppercase tracking-wider font-bold transition-colors inline-flex items-center gap-2 ${
              viewMode === 'gallery' ? 'bg-[#007AFF] text-white' : 'bg-[#131313] text-[#8892B0] border border-white/10 hover:text-white'
            }`}
            data-testid="toggle-gallery"
          >
            <GridFour size={16} weight="bold" /> Gallery
          </button>
        </div>

        {/* Posts View */}
        {viewMode === 'posts' && (
          <>
            {loading ? (
              <div className="text-center py-12 text-[#8892B0]">Loading...</div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 bg-[#131313] border border-white/5">
                <Users size={48} className="mx-auto text-[#5C6B82] mb-4" />
                <p className="text-[#8892B0] mb-2">No posts yet</p>
                <p className="text-sm text-[#5C6B82]">Be the first to share something</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="posts-list">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    className="bg-[#131313] border border-white/5 p-6 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <UserAvatar src={post.user_avatar} name={post.user_name} size={40} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-bold text-sm">{post.user_name}</span>
                          <span className="text-[#5C6B82] text-xs">
                            {new Date(post.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p
                          className="text-[#CCD6F6] whitespace-pre-wrap cursor-pointer hover:text-white transition-colors"
                          onClick={() => loadPostDetails(post.id)}
                        >
                          {post.content}
                        </p>
                        {post.image_url && (
                          <img
                            src={post.image_url}
                            alt="Post attachment"
                            className="mt-3 max-h-64 rounded border border-white/10 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={(e) => { e.stopPropagation(); setLightboxSrc(post.image_url); setLightboxAlt(post.content?.slice(0, 50)); }}
                            data-testid={`post-image-${post.id}`}
                          />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-6 mt-4 pt-4 border-t border-white/5 ml-[52px]">
                      <button
                        onClick={() => handleLike(post.id)}
                        className="flex items-center gap-2 text-[#8892B0] hover:text-[#FF3B30] transition-colors"
                        data-testid={`like-${post.id}`}
                      >
                        <Heart size={18} />
                        <span className="text-sm">{post.likes}</span>
                      </button>
                      <button
                        onClick={() => loadPostDetails(post.id)}
                        className="flex items-center gap-2 text-[#8892B0] hover:text-[#007AFF] transition-colors"
                        data-testid={`comments-${post.id}`}
                      >
                        <ChatCircle size={18} />
                        <span className="text-sm">{post.comments_count}</span>
                      </button>
                      <button
                        onClick={() => { setReportPostId(post.id); setReportDialogOpen(true); }}
                        className="flex items-center gap-2 text-[#5C6B82] hover:text-[#FF3B30] transition-colors ml-auto"
                        data-testid={`report-${post.id}`}
                      >
                        <Flag size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Gallery View */}
        {viewMode === 'gallery' && (
          <>
            {loading ? (
              <div className="text-center py-12 text-[#8892B0]">Loading gallery...</div>
            ) : galleryPosts.length === 0 ? (
              <div className="text-center py-12 bg-[#131313] border border-white/5">
                <Image size={48} className="mx-auto text-[#5C6B82] mb-4" />
                <p className="text-[#8892B0] mb-2">No images shared yet</p>
                <p className="text-sm text-[#5C6B82]">Attach an image to your post to see it here</p>
              </div>
            ) : (
              <div className="columns-1 sm:columns-2 lg:columns-3 gap-4" data-testid="gallery-grid">
                {galleryPosts.map((post) => (
                  <div
                    key={post.id}
                    className="break-inside-avoid mb-4 bg-[#131313] border border-white/5 overflow-hidden group cursor-pointer hover:border-[#007AFF]/50 transition-colors"
                    data-testid={`gallery-item-${post.id}`}
                  >
                    <img
                      src={post.image_url}
                      alt={post.content?.slice(0, 50)}
                      className="w-full object-cover group-hover:opacity-90 transition-opacity"
                      onClick={() => { setLightboxSrc(post.image_url); setLightboxAlt(post.content?.slice(0, 50)); }}
                    />
                    <div className="p-3" onClick={() => loadPostDetails(post.id)}>
                      <div className="flex items-center gap-2 mb-1">
                        <UserAvatar src={post.user_avatar} name={post.user_name} size={20} />
                        <span className="text-[#5C6B82] text-xs">{post.user_name}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-[#8892B0] text-xs">
                          <Heart size={12} /> {post.likes}
                        </span>
                        <span className="flex items-center gap-1 text-[#8892B0] text-xs">
                          <ChatCircle size={12} /> {post.comments_count}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Post Detail Dialog */}
        <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
          <DialogContent className="bg-[#131313] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedPost && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <UserAvatar src={selectedPost.user_avatar} name={selectedPost.user_name} size={36} />
                    <div>
                      <span className="text-white font-bold">{selectedPost.user_name}</span>
                      <p className="text-[#5C6B82] text-xs">
                        {new Date(selectedPost.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <DialogTitle className="sr-only">Post by {selectedPost.user_name}</DialogTitle>
                </DialogHeader>
                <div className="mt-2">
                  <p className="text-[#CCD6F6] whitespace-pre-wrap">{selectedPost.content}</p>
                  {selectedPost.image_url && (
                    <img
                      src={selectedPost.image_url}
                      alt="Post attachment"
                      className="mt-4 max-h-96 rounded border border-white/10 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => { setLightboxSrc(selectedPost.image_url); setLightboxAlt(selectedPost.content?.slice(0, 50)); }}
                      data-testid="detail-post-image"
                    />
                  )}

                  {/* Comments */}
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <h4 className="font-bold text-white mb-4">Comments ({selectedPost.comments?.length || 0})</h4>

                    {selectedPost.comments?.map((comment) => (
                      <div key={comment.id} className="bg-[#0A0A0A] p-4 mb-3 border border-white/5">
                        <div className="flex items-start gap-3">
                          <UserAvatar src={comment.user_avatar} name={comment.user_name} size={28} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-bold text-white">{comment.user_name}</span>
                              <span className="text-xs text-[#5C6B82]">
                                {new Date(comment.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-[#8892B0] text-sm">{comment.content}</p>
                            {comment.image_url && (
                              <img
                                src={comment.image_url}
                                alt="Comment attachment"
                                className="mt-2 max-h-48 rounded border border-white/10 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => { setLightboxSrc(comment.image_url); setLightboxAlt('Comment image'); }}
                                data-testid={`comment-image-${comment.id}`}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {user && (
                      <div className="mt-4">
                        <form onSubmit={handleComment} className="space-y-3">
                          <div className="flex gap-3">
                            <Input
                              value={commentText}
                              onChange={(e) => setCommentText(e.target.value)}
                              placeholder="Add a comment..."
                              className="flex-1 bg-transparent border-white/20 text-white rounded-none"
                              data-testid="comment-input"
                            />
                            <button
                              type="submit"
                              className="px-6 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors"
                              data-testid="comment-submit"
                            >
                              Post
                            </button>
                          </div>
                          {/* Comment Image Upload */}
                          <div className="flex items-center gap-3">
                            <label
                              className="flex items-center gap-2 px-3 py-2 border border-dashed border-white/15 cursor-pointer hover:border-[#007AFF] transition-colors text-xs text-[#8892B0]"
                              data-testid="comment-image-upload-label"
                            >
                              <Image size={16} />
                              {commentUploading ? 'Uploading...' : commentImageUrl ? 'Change image' : 'Attach image'}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleCommentImageUpload}
                                disabled={commentUploading}
                                data-testid="comment-image-file-input"
                              />
                            </label>
                            {commentImageUrl && (
                              <div className="relative inline-block">
                                <img src={commentImageUrl} alt="Preview" className="h-10 rounded border border-white/10 object-cover" />
                                <button
                                  type="button"
                                  onClick={() => setCommentImageUrl('')}
                                  className="absolute -top-1 -right-1 bg-black/80 rounded-full p-0.5 hover:bg-red-600 transition-colors"
                                  data-testid="remove-comment-image-btn"
                                >
                                  <X size={10} className="text-white" />
                                </button>
                              </div>
                            )}
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Report Dialog */}
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="bg-[#131313] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-xl uppercase">Report Post</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div>
                <Label className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">Reason</Label>
                <Textarea
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  placeholder="Why are you reporting this post?"
                  className="bg-transparent border-white/20 text-white rounded-none min-h-[100px]"
                  data-testid="report-reason"
                />
              </div>
              <button
                onClick={handleReport}
                className="w-full py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#cc2f26] transition-colors"
                data-testid="report-submit"
              >
                Submit Report
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Lightbox */}
        {lightboxSrc && (
          <Lightbox
            src={lightboxSrc}
            alt={lightboxAlt}
            onClose={() => { setLightboxSrc(null); setLightboxAlt(''); }}
          />
        )}
      </div>
    </div>
  );
};

export default CommunityPage;
